/**
 * Утилиты для работы с Performance API браузера
 * Используются для точного измерения времени загрузки ресурсов (сетевые запросы, изображения)
 */

/**
 * Нормализует URL для сравнения (убирает хэш, оставляет только pathname и search)
 * @param url - URL для нормализации
 * @returns Нормализованный URL
 */
export const normalizeUrlForComparison: (url: string) => string = (url: string): string => {
  if (typeof window === 'undefined') {
    return url;
  }

  try {
    const urlObj: URL = new URL(url, window.location.origin);
    return `${urlObj.pathname}${urlObj.search}`;
  } catch {
    return url;
  }
};

/**
 * Проверяет доступность Performance API в текущем окружении
 * @returns true, если Performance API доступен
 */
const isPerformanceAPIAvailable: () => boolean = (): boolean => {
  return (
    typeof performance !== 'undefined' &&
    typeof performance.getEntriesByType === 'function' &&
    typeof window !== 'undefined'
  );
};

/**
 * Находит ресурс в Performance API по URL
 * @param url - URL для поиска
 * @param maxEntries - Максимальное количество записей для проверки (по умолчанию 50)
 * @returns PerformanceResourceTiming или null, если не найден
 */
const findResourceInPerformanceAPI: (url: string, maxEntries?: number) => PerformanceResourceTiming | null = (
  url: string,
  maxEntries: number = 50
): PerformanceResourceTiming | null => {
  if (!isPerformanceAPIAvailable()) {
    return null;
  }

  try {
    const normalizedUrl: string = normalizeUrlForComparison(url);

    // Получаем все ресурсы из Performance API
    // Ограничиваем поиск последними записями для производительности
    const allResources: PerformanceResourceTiming[] = performance
      .getEntriesByType('resource')
      .slice(-maxEntries) // Берем последние записи (самые свежие)
      .filter((entry: PerformanceEntry): entry is PerformanceResourceTiming => entry.entryType === 'resource');

    // Ищем запрос по URL (начинаем с конца для поиска самого свежего)
    const resourceTiming: PerformanceResourceTiming | undefined = allResources
      .slice()
      .reverse()
      .find((entry: PerformanceResourceTiming): boolean => {
        const normalizedEntryUrl: string = normalizeUrlForComparison(entry.name);
        return (
          normalizedEntryUrl === normalizedUrl ||
          entry.name === url ||
          entry.name.includes(normalizedUrl) ||
          normalizedUrl.includes(normalizedEntryUrl)
        );
      });

    return resourceTiming ?? null;
  } catch (error) {
    console.warn('[Performance API Utils] Failed to find resource in Performance API:', error);
    return null;
  }
};

/**
 * Проверяет валидность данных PerformanceResourceTiming
 * @param resourceTiming - Ресурс из Performance API
 * @returns true, если данные валидны и доступны
 */
const isValidResourceTiming: (resourceTiming: PerformanceResourceTiming | null) => boolean = (
  resourceTiming: PerformanceResourceTiming | null
): boolean => {
  if (!resourceTiming) {
    return false;
  }

  // Проверяем, что данные доступны (не заблокированы политиками безопасности браузера)
  // Для кросс-доменных запросов может потребоваться заголовок Timing-Allow-Origin
  return (
    resourceTiming.responseEnd > 0 &&
    resourceTiming.requestStart > 0 &&
    resourceTiming.responseEnd >= resourceTiming.requestStart
  );
};

/**
 * Получает точное время сетевого запроса из Performance API браузера
 * Это соответствует времени, которое показывает Network панель браузера
 * @param requestUrl - URL запроса для поиска в Performance API
 * @param fallbackDuration - Fallback значение, если Performance API недоступен или данные не найдены
 * @returns Точное время запроса в миллисекундах или fallbackDuration
 */
export const getNetworkRequestDurationFromPerformanceAPI: (requestUrl: string, fallbackDuration: number) => number = (
  requestUrl: string,
  fallbackDuration: number
): number => {
  const resourceTiming: PerformanceResourceTiming | null = findResourceInPerformanceAPI(requestUrl);

  if (!resourceTiming || !isValidResourceTiming(resourceTiming)) {
    return fallbackDuration;
  }

  // responseEnd - requestStart дает точное время от начала отправки до получения последнего байта
  // Это соответствует времени в Network панели браузера
  const networkDuration: number = Math.round(resourceTiming.responseEnd - resourceTiming.requestStart);

  return networkDuration > 0 ? networkDuration : fallbackDuration;
};

/**
 * Получает точное время загрузки изображения из Performance API браузера
 * @param imageUrl - URL изображения для поиска в Performance API
 * @param fallbackDuration - Fallback значение, если Performance API недоступен или данные не найдены
 * @returns Точное время загрузки в миллисекундах или fallbackDuration
 */
export const getImageLoadDurationFromPerformanceAPI: (imageUrl: string, fallbackDuration: number) => number = (
  imageUrl: string,
  fallbackDuration: number
): number => {
  const resourceTiming: PerformanceResourceTiming | null = findResourceInPerformanceAPI(imageUrl);

  if (!resourceTiming || !isValidResourceTiming(resourceTiming)) {
    return fallbackDuration;
  }

  const networkDuration: number = Math.round(resourceTiming.responseEnd - resourceTiming.requestStart);

  return networkDuration > 0 ? networkDuration : fallbackDuration;
};
